import { resolvePlaceholderText } from "./buildApplyDroppedColumnsAction";
import {
  ARSL_ADD_ROW,
  ARSL_ADD_VARIABLES_TO_COLUMN,
  ARSL_ALIGN_TEXT,
  ARSL_APPLY_DROPPED_COLUMNS,
  ARSL_CELL_UPDATE,
  ARSL_CHANGE_SENT,
  ARSL_LOAD_DATA,
  ARSL_REMOVE_COLUMN,
  ARSL_REMOVE_ROW,
  ARSL_SET_COLUMNS,
  ARSL_SET_CURRENT_DATASET,
  ARSL_SET_ROW_HEIGHT,
  ARSL_SET_TLF_VIEW_MODE,
  ARSL_SET_VARIABLE_FILTER,
  ARSL_TOGGLE_VARIABLE_LIST,
  ArsListingAction,
  ArsListingColumn,
  ArsListingState,
  EMPTY_HEADER_COL_ID,
  HEADER_ROW_ID,
} from "./interface";

/**
 * ARS Listing reducer function
 *
 * @param state - Current ArsListingState
 * @param action - Action to perform on the state
 * @returns Updated ArsListingState
 */
export const arsListingReducer = <T>(
  state: ArsListingState<T>,
  action: ArsListingAction<T>,
): ArsListingState<T> => {
  switch (action.operation) {
    case ARSL_ADD_ROW: {
      const targetIndex = state.bodyData.arsBodyRows.findIndex((row) => row.id === action.rowId);
      const insertAt = targetIndex + 1;
      const updatedRows = [
        ...state.bodyData.arsBodyRows.slice(0, insertAt),
        {
          id: crypto.randomUUID(),
          rowHeight: 34,
          ellipsis: action.type === "ellipsis",
        },
        ...state.bodyData.arsBodyRows.slice(insertAt),
      ];

      return {
        ...state,
        changed: true,
        bodyData: {
          ...state.bodyData,
          arsBodyRows: updatedRows,
        },
      };
    }

    case ARSL_ADD_VARIABLES_TO_COLUMN: {
      const targetColumn = state.bodyData.arsListingColumns.find((col) => col.id === action.colId);
      if (targetColumn) {
        const textToT = state.settings.editorProps.textToT;
        const getTextFromT = state.settings.editorProps.getTextFromT;
        const newDisplayPattern = [
          ...(Array.isArray(targetColumn.displayPattern)
            ? targetColumn.displayPattern.map((dp) => getTextFromT(dp))
            : [getTextFromT(targetColumn.displayPattern)]),
          ...action.variables.map((variable) => resolvePlaceholderText(variable.dataType)),
        ].join("/");
        const newHeaderText = [
          getTextFromT(targetColumn.headerText),
          ...action.variables.map((variable) =>
            variable.description?.trim() !== "" ? variable.description : variable.name,
          ),
        ].join("/");
        const newResultPattern = [
          targetColumn.resultPattern,
          ...action.variables.map((variable) => `{${variable.name.toLowerCase()}}`),
        ].join("/");
        const newColumn: ArsListingColumn<T> = {
          ...targetColumn,
          displayPattern: textToT(newDisplayPattern),
          resultPattern: newResultPattern,
          headerText: textToT(newHeaderText),
          variables: [
            ...targetColumn.variables,
            ...action.variables.map((variable) => ({
              dataset: variable.datasetName,
              variable: variable.name,
            })),
          ],
        };
        return {
          ...state,
          changed: true,
          bodyData: {
            ...state.bodyData,
            arsListingColumns: state.bodyData.arsListingColumns.map((col) =>
              col.id === action.colId ? newColumn : col,
            ),
          },
        };
      }
      return state;
    }

    case ARSL_ALIGN_TEXT: {
      const updatedColumns = state.bodyData.arsListingColumns.map((column) =>
        column.id === action.colId
          ? {
              ...column,
              [action.isHeader ? "headerTextAlignment" : "bodyTextAlignment"]: action.alignment,
            }
          : column,
      );

      return {
        ...state,
        changed: true,
        bodyData: {
          ...state.bodyData,
          arsListingColumns: updatedColumns,
        },
      };
    }

    case ARSL_APPLY_DROPPED_COLUMNS: {
      if (state.bodyData.arsListingColumns.length >= 20) {
        return state;
      }
      const existingColumns = state.bodyData.arsListingColumns;

      if (existingColumns.length === 0 || action.colId === EMPTY_HEADER_COL_ID) {
        return {
          ...state,
          changed: true,
          bodyData: {
            ...state.bodyData,
            arsListingColumns: action.columns.slice(0, 20),
          },
        };
      }

      const targetIndex = existingColumns.findIndex((column) => column.id === action.colId);

      let updatedColumns: typeof existingColumns;

      if (targetIndex < 0) {
        updatedColumns = [...existingColumns, ...action.columns];
      } else if (action.dropAction === "insert-before") {
        updatedColumns = [
          ...existingColumns.slice(0, targetIndex),
          ...action.columns,
          ...existingColumns.slice(targetIndex),
        ];
      } else if (action.dropAction === "insert-after") {
        updatedColumns = [
          ...existingColumns.slice(0, targetIndex + 1),
          ...action.columns,
          ...existingColumns.slice(targetIndex + 1),
        ];
      } else {
        const [replacementColumn, ...remainingColumns] = action.columns;
        const nextColumns = [...existingColumns];
        nextColumns[targetIndex] = replacementColumn;
        if (remainingColumns.length > 0) {
          nextColumns.splice(targetIndex + 1, 0, ...remainingColumns);
        }
        updatedColumns = nextColumns;
      }

      return {
        ...state,
        changed: true,
        bodyData: {
          ...state.bodyData,
          arsListingColumns: updatedColumns.slice(0, 20),
        },
      };
    }

    case ARSL_CELL_UPDATE: {
      const thisCol = state.bodyData.arsListingColumns.find((col) => col.id === action.colId);
      // Return if the target column is not found
      if (thisCol) {
        // Update header text if the updated cell is in the header row
        if (action.rowId === HEADER_ROW_ID) {
          if (state.settings.tlfViewMode.value !== "ANNOTATIONS") {
            const updatedColumn = {
              ...thisCol,
              headerText: action.value,
            };
            return {
              ...state,
              changed: true,
              bodyData: {
                ...state.bodyData,
                arsListingColumns: state.bodyData.arsListingColumns.map((col) =>
                  col.id === action.colId ? updatedColumn : col,
                ),
              },
            };
          }
        }
        // otherwise update the display pattern
        else {
          const updatedColumn: ArsListingColumn<T> = {
            ...thisCol,
            displayPattern:
              state.settings.tlfViewMode.value !== "ANNOTATIONS"
                ? action.value
                : thisCol.displayPattern,
            resultPattern:
              state.settings.tlfViewMode.value === "ANNOTATIONS"
                ? state.settings.editorProps.getTextFromT(action.value).join("\n")
                : thisCol.resultPattern,
          };
          return {
            ...state,
            changed: true,
            bodyData: {
              ...state.bodyData,
              arsListingColumns: state.bodyData.arsListingColumns.map((col) =>
                col.id === action.colId ? updatedColumn : col,
              ),
            },
          };
        }
      }
      return state;
    }

    case ARSL_CHANGE_SENT:
      return {
        ...state,
        changed: false,
      };

    case ARSL_LOAD_DATA:
      return {
        ...state,
        bodyData: action.bodyData,
        changed: false,
      };

    case ARSL_REMOVE_COLUMN:
      return state.bodyData.arsListingColumns.length <= 1
        ? state
        : {
            ...state,
            changed: true,
            bodyData: {
              ...state.bodyData,
              arsListingColumns: state.bodyData.arsListingColumns.filter(
                (column) => column.id !== action.colId,
              ),
            },
          };

    case ARSL_REMOVE_ROW:
      return state.bodyData.arsBodyRows.length <= 1
        ? state
        : {
            ...state,
            changed: true,
            bodyData: {
              ...state.bodyData,
              arsBodyRows: state.bodyData.arsBodyRows.filter((row) => row.id !== action.rowId),
            },
          };

    case ARSL_SET_COLUMNS:
      return {
        ...state,
        changed: true,
        bodyData: {
          ...state.bodyData,
          arsListingColumns: action.columns.slice(0, 20),
        },
      };

    case ARSL_SET_CURRENT_DATASET:
      return {
        ...state,
        settings: {
          ...state.settings,
          currentDatasetId: action.datasetId,
        },
      };

    case ARSL_SET_ROW_HEIGHT:
      return {
        ...state,
        changed: true,
        bodyData: {
          ...state.bodyData,
          arsBodyRows: state.bodyData.arsBodyRows.map((row) =>
            row.id === action.rowId ? { ...row, rowHeight: action.rowHeight } : row,
          ),
        },
      };

    case ARSL_SET_TLF_VIEW_MODE:
      return {
        ...state,
        settings: {
          ...state.settings,
          tlfViewMode: action.tlfViewMode,
        },
      };

    case ARSL_SET_VARIABLE_FILTER:
      return {
        ...state,
        settings: {
          ...state.settings,
          variableFilter: action.variableFilter,
        },
      };

    case ARSL_TOGGLE_VARIABLE_LIST:
      return {
        ...state,
        settings: {
          ...state.settings,
          showVariableList: !state.settings.showVariableList,
        },
      };
  }
};
